import { useEffect, useState } from 'react';
import {
  useDispatch,
  useLab,
  useLabCurrency,
  useLabProducts,
  useOrder,
  useOrderProduct,
} from '@witmetrics/data-store';
import { isBlank } from '@witmetrics/utils/strings';
import { findById } from '@witmetrics/utils/arrays';
import { buildProductOption, filterOptions } from '@witmetrics/unison-core';
import { isNullish } from '@witmetrics/utils/misc';
import { parseLocaleNumber } from '@witmetrics/utils/numbers';
import { showErrorToaster, showSuccessToaster } from '@/utils/toasters';
import { useString } from '@/hooks/useString';
import { useToggle } from '@/hooks/useToggle';
import { useAppState } from '@/providers/AppStateProvider';
import PriceInput from '@/components/inputs/PriceInput';
import TextField from '@/components/inputs/TextField';
import Dialog, {
  DefaultDialogActions,
  DialogBody,
  DialogTitle,
} from '@/components/Dialog';
import SearchableDropdown from '@/components/inputs/SearchableDropdown';
import {
  classes,
  createNewOrderProduct,
  fetchLabProducts,
  sortProducts,
  updateOrderProduct,
} from './utils';

export type OrderProductDialogProps = {
  orderId: number;
  orderProductId?: number;
  onClose: () => void;
};

export default function OrderProductDialog({
  orderId,
  orderProductId,
  onClose,
}: OrderProductDialogProps) {
  const dispatch = useDispatch();
  const { onApiError } = useAppState();
  const order = useOrder(orderId);
  const lab = useLab(order?.labId);
  const labProducts = useLabProducts(lab?.id);
  const currency = useLabCurrency(lab?.id);
  const initialOrderProduct = useOrderProduct(orderProductId);
  const [product, setProduct] = useState(
    initialOrderProduct ? buildProductOption(initialOrderProduct.product) : null
  );
  const [price, setPrice] = useString(initialOrderProduct?.price || '');
  const [comments, setComments] = useString(
    initialOrderProduct?.comments || ''
  );
  const [isSaving, toggleSaving] = useToggle();

  useEffect(() => {
    if (lab) handleFetchProducts();
  }, [lab]);

  const handleFetchProducts = async () => {
    try {
      dispatch.addProducts(await fetchLabProducts(lab!.id));
    } catch (err) {
      onApiError(err, 'Error fetching products', () => {
        handleFetchProducts();
      });
    }
  };

  const handleSave = async () => {
    try {
      if (!product || isBlank(price)) {
        return showErrorToaster('Please complete all required fields');
      }
      toggleSaving(true);
      if (orderProductId) {
        dispatch.addOrderProducts([
          await updateOrderProduct(
            orderProductId,
            product.value,
            price,
            comments
          ),
        ]);
        showSuccessToaster('Product updated');
      } else {
        dispatch.addOrderProducts([
          await createNewOrderProduct(orderId, product.value, price, comments),
        ]);
        showSuccessToaster('Product added');
      }

      onClose();
    } catch (err) {
      toggleSaving(false);
      onApiError(err, 'Error saving product', () => handleSave());
    }
  };

  const handleSelect = (productId?: number) => {
    if (isNullish(productId)) return setProduct(null);
    const selectedProduct = findById(labProducts, productId)!;
    setProduct(buildProductOption(selectedProduct));
    if (isBlank(price)) setPrice(selectedProduct.price);
  };

  const isSaveDisabled = () => {
    return isSaving || !product || isBlank(price);
  };

  return (
    <Dialog className={classes.dialog} onClose={onClose}>
      <DialogTitle
        title={orderProductId ? 'Edit Product' : 'Add Product'}
        onClose={onClose}
      />
      <DialogBody className={classes.body}>
        <SearchableDropdown<number>
          className={classes.input}
          label="Product"
          value={product}
          initialOptions={sortProducts(labProducts)}
          onSearch={async (search) =>
            filterOptions(search, sortProducts(labProducts))
          }
          onSelect={(update) => handleSelect(update?.value)}
        />
        <PriceInput
          className={classes.input}
          size="small"
          name="price"
          label="Price"
          value={{
            label: isNullish(price) ? '' : price,
            value: parseLocaleNumber(price),
          }}
          currency={currency}
          onChange={(update) => setPrice(update.label)}
        />
        <TextField
          multiline
          className={classes.comments}
          minRows={3}
          maxRows={5}
          label="Comments (optional)"
          value={comments}
          onChange={setComments}
        />
      </DialogBody>
      <DefaultDialogActions
        disabled={isSaveDisabled()}
        pending={isSaving}
        confirmLabel="Save"
        pendingLabel="Saving"
        onCancel={onClose}
        onConfirm={handleSave}
      />
    </Dialog>
  );
}
